import axios from 'axios';
import { BACKEND_URL } from '../config_and_helpers/config';
import { showToast } from '../config_and_helpers/helpers';
import QuoteCache from '../config_and_helpers/quote_cache';
import { logInfo, logError } from '../config_and_helpers/logger';
import { normalizeCatalogEmailResponse } from '../utils/catalogQuoteEmailResponse';
import {
  formatUploadFinalizeErrors,
  notifyUploadFinalizeFailure,
} from '../utils/uploadFinalizeErrors';
import { isCanceledRequest } from '../utils/listSearchRequest';

/**
 * Toast + cache invalidation when a single-quote upload finalize fails.
 *
 * @param {number|string} quoteId
 * @param {{ success?: boolean, messages?: string[], error?: unknown }|null} result
 */
function handleQuoteFinalizeFailure(quoteId, result) {
  if (!result || result.success) return;
  notifyUploadFinalizeFailure(result.messages || result.error);
  invalidateQuoteDetailsCache(quoteId);
}

/**
 * Drop cached get_details payload so the next lazy load hits the API.
 *
 * @param {number|string|null|undefined} quoteId
 */
export const invalidateQuoteDetailsCache = quoteId => {
  if (quoteId == null || quoteId === '') return;
  const id = Number(quoteId);
  QuoteCache.remove(id);
  logInfo(LOG_CONTEXT_QUOTE, 'invalidateQuoteDetailsCache', { quoteId: id });
};

/**
 * Toast + cache invalidation when batch quote upload finalize fails.
 *
 * @param {Record<string, string>} uploadStatuses
 * @param {{ success?: boolean, messages?: string[], error?: unknown }|null} result
 */
function handleBatchQuoteFinalizeFailure(uploadStatuses, result) {
  if (!result || result.success) return;
  notifyUploadFinalizeFailure(result.messages || result.error);
  if (uploadStatuses && typeof uploadStatuses === 'object') {
    Object.entries(uploadStatuses).forEach(([id, status]) => {
      if (status === 'completed') QuoteCache.remove(parseInt(id, 10));
    });
  }
}

const LOG_CONTEXT_QUOTE = 'quote_client';

export { normalizeCatalogEmailResponse } from '../utils/catalogQuoteEmailResponse';

/**
 * Update the upload status of a quote.
 *
 * This function sends a POST request to the 'quotes/update_quote_upload_status/' endpoint of the backend API, providing the quoteId and
 * an uploadStatus value. The quoteId is used to identify the quote and the uploadStatus value informs the backend as to what the
 * new status value is.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {number} quoteId - The ID of quote to finalize the upload status for.
 * @param {string} uploadStatus - The new upload status, 'completed' or 'failed'.
 * @returns {Promise<Object>} An object with a boolean 'success' attribute. If the update is not successful, it returns an array of error messages.
 * @throws Will throw an error if the quote upload status update request fails.
 */
export const finalizeQuoteUploadStatus = async (
  token,
  quoteId,
  uploadStatus
) => {
  try {
    // Make a POST request to the backend API endpoint to update the quote upload status
    await axios.post(
      `${BACKEND_URL}quotes/update_quote_upload_status/`,
      // This time, the quote_id and status are sent as JSON payload in a POST request.
      { quote_id: quoteId, status: uploadStatus },
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    // If the request is successful and the status got updated, return a success status
    return { success: true };

    // If any error occurs during the status update
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT_QUOTE, 'finalizeQuoteUploadStatus failed', {
      quoteId,
      status: error.response?.status,
    });
    return {
      success: false,
      error: errPayload,
      messages: formatUploadFinalizeErrors(errPayload),
    };
  }
};

/**
 * Upload a file for a specific quote to Amazon S3.
 *
 * This function takes as arguments a presignedPostData object (containing details about the S3 upload) and a file to be uploaded.
 * It creates a FormData object to hold the details from presignedPostData and the file. Then it sends this FormData object to the
 * presigned URL (which is obtained from presignedPostData) via a POST request. If the request is successful, it returns an object indicating
 * that the upload status is 'completed'. If the request is not successful, it returns an object indicating that the upload status is 'failed'.
 *
 * @async
 * @param {Object} presignedPostData - The pre-signed POST data from AWS S3.
 * @param {File} file - The file to upload.
 * @returns {Promise<Object>} - An object with the `uploadStatus` of the file ('completed' or 'failed'). Any error messages are converted into an array and returned.
 * @throws Will throw an error if the file upload request fails.
 */
export const uploadQuoteFileToS3 = async (presignedPostData, file) => {
  // Create a new FormData instance
  const formData = new FormData();

  // Iterate over the presignedPostData fields
  // and append each field to the formData object.
  Object.keys(presignedPostData.fields).forEach(key => {
    formData.append(key, presignedPostData.fields[key]);
  });

  // Append the file to the formData object
  formData.append('file', file);

  // Try to send the formData to the presigned URL via POST
  try {
    const response = await axios.post(presignedPostData.url, formData);

    // If the status code of the response is in the success range (200-299)
    // return an object with `uploadStatus` set to "completed"
    if (response.status >= 200 && response.status < 300) {
      return { uploadStatus: 'completed' };

      // If the status code of the response is not in the success range
      // return an object with `uploadStatus` set to "failed"
    } else {
      return { uploadStatus: 'failed' };
    }

    // If an error occurs during the file upload
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT_QUOTE, 'uploadQuoteFileToS3 failed', {
      error: errPayload,
    });
    return { uploadStatus: 'failed', error: errPayload };
  }
};

/**
 * Flatten DRF validation errors from catalog quote request endpoints into display strings.
 *
 * @param {Object|Array|string|null} errPayload - `error.response.data` or similar.
 * @returns {string[]} User-facing error messages.
 */
export const formatCatalogQuoteRequestErrors = errPayload => {
  if (errPayload == null) {
    return ['Request failed. Please try again.'];
  }
  if (typeof errPayload === 'string') {
    return [errPayload];
  }
  if (Array.isArray(errPayload)) {
    return errPayload.map(item => String(item));
  }
  const messages = [];
  Object.entries(errPayload).forEach(([key, value]) => {
    if (value == null) return;
    if (Array.isArray(value)) {
      value.forEach(v => messages.push(`${key}: ${v}`));
    } else if (typeof value === 'object') {
      messages.push(`${key}: ${JSON.stringify(value)}`);
    } else {
      messages.push(String(value));
    }
  });
  return messages.length ? messages : ['Request failed. Please try again.'];
};

/**
 * Send supplier quote-request email for a single catalog product (no Quote record).
 *
 * @async
 * @param {string} token
 * @param {number|string} productId
 * @param {Object} payload
 * @param {number} payload.quantity - Required integer >= 1.
 * @returns {Promise<{success:boolean,data?:{corporate_demand_ref:string,suppliers:Array},error?:Object,messages?:string[]}>}
 */
export const requestSupplierQuoteFromProduct = async (token, productId, payload) => {
  try {
    logInfo(LOG_CONTEXT_QUOTE, 'requestSupplierQuoteFromProduct start', {
      productId,
      quantity: payload?.quantity,
    });
    const response = await axios.post(
      `${BACKEND_URL}products/${productId}/request_supplier_quote/`,
      payload,
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    const data = normalizeCatalogEmailResponse(response.data);
    logInfo(LOG_CONTEXT_QUOTE, 'requestSupplierQuoteFromProduct success', {
      productId,
      corporateDemandRef: data.corporate_demand_ref,
      supplierCount: data.suppliers.length,
      emailSent: data.suppliers.map(s => s.emailSent),
    });
    return { success: true, data };
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT_QUOTE, 'requestSupplierQuoteFromProduct failed', {
      productId,
      error: errPayload,
    });
    return {
      success: false,
      error: errPayload,
      messages: formatCatalogQuoteRequestErrors(errPayload),
    };
  }
};

/**
 * Batch send supplier quote-request emails from catalog product lines (no Quote records).
 *
 * @async
 * @param {string} token
 * @param {Object} payload
 * @param {Array<{product_id:number,quantity:number}>} payload.lines
 * @param {number} [payload.order_request_id] - When all lines originate from one procurement
 *   order request, pass its id so the backend can attach request context to supplier emails.
 * @param {number} [payload.project_id] - Optional project context for catalog quote emails.
 * @returns {Promise<{success:boolean,data?:{corporate_demand_ref:string,suppliers:Array},error?:Object,messages?:string[]}>}
 */
/**
 * Send supplier quote-request emails for standalone custom (ad-hoc) lines (no project).
 *
 * @async
 * @param {string} token
 * @param {Object} payload
 * @param {Array<Object>} payload.lines - Custom line fields per standalone handoff doc.
 * @param {string} [payload.contact_name] - One-time lab contact name for this send only.
 * @param {string} [payload.contact_email] - One-time lab contact email for this send only.
 * @returns {Promise<{success:boolean,data?:Object,error?:Object,messages?:string[],status?:number}>}
 */
export const requestAdHocQuoteEmails = async (token, payload) => {
  try {
    logInfo(LOG_CONTEXT_QUOTE, 'requestAdHocQuoteEmails start', {
      lineCount: Array.isArray(payload?.lines) ? payload.lines.length : 0,
      hasOneTimeLabContact: Boolean(
        payload?.contact_name && payload?.contact_email
      ),
    });
    const response = await axios.post(
      `${BACKEND_URL}quotes/request_ad_hoc_quote_emails/`,
      payload,
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    const data = normalizeCatalogEmailResponse(response.data);
    logInfo(LOG_CONTEXT_QUOTE, 'requestAdHocQuoteEmails success', {
      corporateDemandRef: data.corporate_demand_ref,
      supplierCount: data.suppliers.length,
    });
    return { success: true, data };
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    const status = error.response?.status;
    logError(LOG_CONTEXT_QUOTE, 'requestAdHocQuoteEmails failed', {
      status,
      error: errPayload,
    });
    let messages = formatCatalogQuoteRequestErrors(errPayload);
    if (status === 404 || status === 501) {
      messages = [
        'Standalone custom line quote emails are not available on the server yet.',
      ];
    }
    return {
      success: false,
      status,
      error: errPayload,
      messages,
    };
  }
};

export const requestQuotesFromCatalog = async (token, payload) => {
  try {
    logInfo(LOG_CONTEXT_QUOTE, 'requestQuotesFromCatalog start', {
      lineCount: Array.isArray(payload?.lines) ? payload.lines.length : 0,
      orderRequestId: payload?.order_request_id ?? null,
      projectId: payload?.project_id ?? null,
    });
    const response = await axios.post(
      `${BACKEND_URL}quotes/request_from_catalog/`,
      payload,
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    const data = normalizeCatalogEmailResponse(response.data);
    logInfo(LOG_CONTEXT_QUOTE, 'requestQuotesFromCatalog success', {
      corporateDemandRef: data.corporate_demand_ref,
      supplierCount: data.suppliers.length,
      emailSent: data.suppliers.map(s => s.emailSent),
    });
    return { success: true, data };
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT_QUOTE, 'requestQuotesFromCatalog failed', {
      error: errPayload,
    });
    return {
      success: false,
      error: errPayload,
      messages: formatCatalogQuoteRequestErrors(errPayload),
    };
  }
};

/**
 * Create a quote based on the items in the cart.
 *
 * @deprecated Shop checkout should use `requestQuotesFromCatalog`. Legacy POST `/quotes/` grouped cart JSON.
 * @async
 * @param {string} token - The access token for the current user.
 * @param {Array} cartItems - The list of items in the cart.
 * @returns {Promise<Object>} - An object with a boolean `success` attribute and a `toast` method. For unsuccessful actions, it returns an array of error messages.
 * @throws Will throw an error if the create quote request fails.
 */
export const createQuoteFromCart = async (token, cart_items) => {
  // Try to send a POST request to the backend API to create a new quote
  try {
    await axios.post(
      `${BACKEND_URL}quotes/`,
      // JSON.stringify is used to convert the JavaScript object of cart items into a JSON string
      JSON.stringify(cart_items),
      // The `headers` contain the bearer token for authentication and the content type of the body
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    // If the request is successful, return an object with a 'success' attribute and a 'toast' method
    // The 'toast' method shows a success toast about the successful shopping when called
    return {
      success: true,
      toast: () =>
        showToast('Shopping Successful!', 'success', 'top-right', 3000),
    };

    // If any error occurs during the creation of the quote
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT_QUOTE, 'createQuoteFromCart failed', {
      error: errPayload,
    });
    return { success: false, error: errPayload };
  }
};

/**
 * Manually create a quote.
 *
 * This function sends a POST request to the backend API to create a new quote using the provided quoteData. If quoteFile is provided, this function attempts
 * to upload the file to Amazon S3 using the presigned URL and quoteId returned by the backend, and update the upload status of the quote.
 * If everything goes as planned, it returns an object with the keys 'success' (true) and 'toast' (a function that shows a success toast). However, if any step fails,
 * it returns an object with 'success' set to false.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {Object} quoteData - Quote fields. Funding: send `budget` OR `funding_credit_id` + `funding_amount` (mutually exclusive).
 * @param {File} quoteFile - The attached file for the quote.
 * @returns {Promise<Object>} - An object with a boolean 'success' attribute and a 'toast' function. For unsuccessful actions, it returns an array of error messages.
 * @throws Will throw an error if the create quote request or any subsequent action fails.
 */
export const createQuoteManually = async (token, quoteData, quoteFile) => {
  // Try to send a POST request to create a new quote with the provided data
  try {
    const requestQuoteData = { ...(quoteData || {}) };
    const itemsBefore = requestQuoteData.items;
    const itemsTypeBefore = Array.isArray(itemsBefore)
      ? 'array'
      : typeof itemsBefore;
    if (itemsBefore != null && typeof itemsBefore !== 'string') {
      requestQuoteData.items = JSON.stringify(itemsBefore);
    }
    const itemsSent = requestQuoteData.items;
    logInfo(LOG_CONTEXT_QUOTE, 'createQuoteManually: items serialization summary', {
      itemsTypeBefore,
      itemsTypeSent: Array.isArray(itemsSent) ? 'array' : typeof itemsSent,
      itemsCountIfArray: Array.isArray(itemsBefore) ? itemsBefore.length : null,
      itemsStringLengthIfSent:
        typeof itemsSent === 'string' ? itemsSent.length : null,
    });

    const response = await axios.post(`${BACKEND_URL}quotes/`, requestQuoteData, {
      headers: {
        Authorization: `Token ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });

    // Prepare the result object with a success status
    let result = { success: true };

    // If a quote file is provided
    if (quoteFile) {
      // Get the presigned URL and quote ID from the response
      const presignedUrl = response.data.presigned_url;
      const quoteId = response.data.id;

      // If the response is valid and contains presignedUrl and quoteId
      if (response && presignedUrl && quoteId) {
        // Upload the quote file to S3
        uploadQuoteFileToS3(presignedUrl, quoteFile).then(r => {
          // If the upload is successful, finalize quote upload status
          if (r && r.uploadStatus) {
            finalizeQuoteUploadStatus(token, quoteId, r.uploadStatus).then(
              finalizeRes => {
                if (finalizeRes && !finalizeRes.success) {
                  result.success = false;
                  handleQuoteFinalizeFailure(quoteId, finalizeRes);
                }
              }
            );
          } else {
            // If the upload was not successful, set the success status to false
            result.success = false;
          }
        });
      } else {
        // If response, presignedUrl, or quoteId are invalid, set the success status to false
        result.success = false;
      }
    }

    // Prepare a toast message for successful quote creation
    result.toast = () =>
      showToast('Quote created successfully!', 'success', 'top-right', 3000);

    // Return the result
    return result;

    // If any error occurs during the creation of the quote
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT_QUOTE, 'createQuoteManually failed', {
      error: errPayload,
    });
    return { success: false, error: errPayload };
  }
};

/**
 * Normalize backend OCR parse response for supplier quote review UI.
 * Returns stable arrays/objects so the review step can render safely.
 *
 * Response `mode` is authoritative (`products` | `prepayment`). Older backends may
 * send `quote_type: prepayment` instead; that legacy field is mapped to `mode` only
 * here — callers must not send `quote_type` on parse requests.
 *
 * @param {Object} raw - Raw JSON from `parse_supplier_quote_ocr` (success or error body).
 * @returns {Object} Normalized review contract: `mode`, `header_fields`, `items`, flags, etc.
 */
export const normalizeSupplierQuoteOcrResponse = raw => {
  const source = raw || {};
  const supplierCandidate = source.supplier_candidate || null;
  const supplierCandidates = Array.isArray(source.supplier_candidates)
    ? source.supplier_candidates
    : supplierCandidate
      ? [supplierCandidate]
      : [];
  const items = Array.isArray(source.items) ? source.items : [];
  const itemsNeedingReview = Array.isArray(source.items_needing_review)
    ? source.items_needing_review
    : items.filter(i => i?.needs_review === true);
  const createdProductsPreview = Array.isArray(source.created_products_preview)
    ? source.created_products_preview
    : [];
  const globalIssues = Array.isArray(source.global_issues)
    ? source.global_issues
    : [];
  const headerFields = source.header_fields || {};
  const sourceSummary = source.source_summary || {};

  const usedLegacyQuoteType =
    source.mode !== 'prepayment' &&
    source.mode !== 'products' &&
    source.quote_type === 'prepayment';
  const mode =
    source.mode === 'prepayment' || usedLegacyQuoteType
      ? 'prepayment'
      : 'products';
  if (usedLegacyQuoteType) {
    logInfo(LOG_CONTEXT_QUOTE, 'normalizeSupplierQuoteOcrResponse: legacy quote_type', {
      mappedMode: mode,
    });
  }

  return {
    mode,
    supplier_candidate: supplierCandidate,
    supplier_candidates: supplierCandidates,
    items,
    items_needing_review: itemsNeedingReview,
    created_products_preview: createdProductsPreview,
    header_fields: {
      corporate_demand_ref: headerFields.corporate_demand_ref || '',
      quote_reference: headerFields.quote_reference ?? null,
      budget: headerFields.budget ?? '',
      prepayment_amount: headerFields.prepayment_amount ?? null,
      prepayment_currency: headerFields.prepayment_currency ?? null,
    },
    source_summary: sourceSummary,
    global_issues: globalIssues,
    requires_manual_review: source.requires_manual_review === true,
  };
};

/**
 * Parse supplier quote OCR payload and return review contract for UI.
 * POST `quotes/parse_supplier_quote_ocr/` with multipart `file` and `mode`.
 *
 * @async
 * @param {string} token - Auth token.
 * @param {File|Blob|{file:File}} fileOrSource - PDF to parse.
 * @param {Object} [options]
 * @param {'products'|'prepayment'} [options.mode] - Parser mode; default `products`.
 * @param {'products'|'prepayment'} [options.quoteType] - Deprecated alias for `mode` (prefer `mode`).
 * @returns {Promise<{success:boolean,data?:Object,message?:string,error?:string,details?:Object}>}
 */
export const parseSupplierQuoteOcr = async (token, fileOrSource, options = {}) => {
  const mode = options.mode || options.quoteType || 'products';
  if (options.quoteType && !options.mode) {
    logInfo(LOG_CONTEXT_QUOTE, 'parseSupplierQuoteOcr: deprecated quoteType option', {
      mode,
    });
  }
  try {
    const requestData = new FormData();
    if (fileOrSource instanceof File || fileOrSource instanceof Blob) {
      requestData.append('file', fileOrSource);
    } else if (fileOrSource?.file) {
      requestData.append('file', fileOrSource.file);
    } else {
      return {
        success: false,
        error: 'Invalid input',
        message: 'A supplier quote file is required for OCR parsing.',
      };
    }

    requestData.append('mode', mode);
    logInfo(LOG_CONTEXT_QUOTE, 'parseSupplierQuoteOcr start', { mode });

    const response = await axios.post(
      `${BACKEND_URL}quotes/parse_supplier_quote_ocr/`,
      requestData,
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'multipart/form-data',
        },
        timeout: 300000,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      }
    );

    const normalized = normalizeSupplierQuoteOcrResponse(response.data);
    logInfo(LOG_CONTEXT_QUOTE, 'parseSupplierQuoteOcr success', {
      requestMode: mode,
      responseMode: normalized.mode,
      supplierCandidates: normalized.supplier_candidates.length,
      itemsCount: normalized.items.length,
      itemsNeedingReview: normalized.items_needing_review.length,
      requiresManualReview: normalized.requires_manual_review,
    });
    return { success: true, data: normalized };
  } catch (error) {
    const details = error.response?.data || {};
    const normalized =
      typeof details === 'object' && details
        ? normalizeSupplierQuoteOcrResponse(details)
        : null;
    logError(LOG_CONTEXT_QUOTE, 'parseSupplierQuoteOcr failed', {
      requestMode: mode,
      status: error.response?.status,
      message: error.message,
      responseMode: normalized?.mode ?? null,
      requiresManualReview: normalized?.requires_manual_review ?? null,
    });
    return {
      success: false,
      error: `HTTP ${error.response?.status || 'ERROR'}`,
      message:
        details?.message ||
        error.message ||
        'Failed to parse supplier quote OCR.',
      details: normalized || details,
    };
  }
};

/**
 * Update an existing quote.
 *
 * This function sends a PATCH request to the backend API to update an existing quote using the quoteId and updatedQuoteData.
 * If quoteFile is provided, the function attempts to upload the file to Amazon S3 using the presigned URL and quoteId returned by the backend,
 * and update the upload status of the quote.
 * If everything goes as planned, it returns an object with the keys 'success' (true) and 'toast' (a function that shows a success toast).
 * However, if any step fails, it returns an object with 'success' set to false.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {string} quoteId - The ID of the quote to update.
 * @param {Object} updatedQuoteData - Quote fields. Funding: send `budget` OR `funding_credit_id` + `funding_amount` (mutually exclusive).
 * @param {File} quoteFile - The attached file for the quote.
 * @returns {Promise<Object>} - An object with a boolean 'success' attribute and a 'toast' function. For unsuccessful actions, it returns an array of error messages.
 * @throws Will throw an error if the update quote request or any subsequent action fails.
 */
export const updateQuote = async (
  token,
  quoteId,
  updatedQuoteData,
  quoteFile
) => {
  // Try to send a PATCH request to update a quote with the specified quoteId
  try {
    const response = await axios.patch(
      `${BACKEND_URL}quotes/${quoteId}/`,
      updatedQuoteData,
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    // Prepare the result object with a success status
    let result = { success: true, updatedQuote: response.data };
    invalidateQuoteDetailsCache(quoteId);

    // If a new quote file is attached
    if (quoteFile) {
      // Extract the presigned URL and quote ID from the response
      const presignedUrl = response.data.presigned_url;
      const quoteId = response.data.id;

      // If the response is valid and contains presignedUrl and quoteId
      if (response && presignedUrl && quoteId) {
        // Upload the new quote file to S3
        uploadQuoteFileToS3(presignedUrl, quoteFile).then(r => {
          // If the upload is successful, finalize quote upload status
          if (r && r.uploadStatus) {
            finalizeQuoteUploadStatus(token, quoteId, r.uploadStatus).then(
              finalizeRes => {
                if (finalizeRes && !finalizeRes.success) {
                  result.success = false;
                  handleQuoteFinalizeFailure(quoteId, finalizeRes);
                }
              }
            );
          } else {
            // If the upload was not successful, set the success status to false
            result.success = false;
          }
        });
      } else {
        // If response, presignedUrl, or quoteId are invalid, set the success status to false
        result.success = false;
      }
    }

    // Prepare a toast message for successful quote update
    result.toast = () =>
      showToast('Quote updated successfully!', 'success', 'top-right', 3000);

    // Return the result
    return result;

    // If any error occurs during the quote update
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT_QUOTE, 'updateQuote failed', { error: errPayload });
    return { success: false, error: errPayload };
  }
};

/**
 * Delete a quote.
 *
 * This function sends a DELETE request to the backend API to delete an existing quote using the quoteId.
 * If the request is successful, it returns an object with the keys 'success' (true) and 'toast' (a function that shows a success toast).
 * If any step fails, it returns an array of error messages.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {string} quoteId - The ID of the quote to delete.
 * @returns {Promise<Object>} - An object with a boolean 'success' attribute and a 'toast' function. For unsuccessful actions, it returns an array of error messages.
 * @throws Will throw an error if the delete quote request fails.
 */
export const deleteQuote = async (token, quoteId) => {
  // Try to send a DELETE request to delete a specific quote using the provided quoteId
  try {
    await axios.delete(`${BACKEND_URL}quotes/${quoteId}/`, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });

    invalidateQuoteDetailsCache(quoteId);

    // If the request is successful, return an object with success set to true and a toast function
    // which shows a success toast when the quote has been deleted
    return {
      success: true,
      toast: () =>
        showToast('Quote deleted successfully!', 'success', 'top-right', 3000),
    };

    // If any error occurs during the deletion
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT_QUOTE, 'deleteQuote failed', { error: errPayload });
    return { success: false, error: errPayload };
  }
};

/**
 * Fetch a list of quotes.
 *
 * This function sends a GET request to the backend API to fetch a list of quotes. Optionally, a search input text might be added to the request
 * URL to implement search functionality. The fetched quotes are stored using the setQuotes function (most likely setting the state in a React component).
 * The function fetches from the next page as long as there is one. It returns an object indicating the success of the operation and the URL of the next page,
 * if it exists. If there is no next page, it returns an object indicating the success of the operation and that the end has been reached.
 * Any errors during the request are logged and returned.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {Function} setQuotes - Function updating the `quotes` state.
 * @param {Object} options - Optional parameters for the request. It could include `searchInput` for search functionality and `nextPage` for pagination.
 * @returns {Promise<Object>} - An object with a boolean 'success' attribute, possibly including 'nextPage' and/or 'reachedEnd' attributes. Returns an array of error messages if unsuccessful.
 * @throws Will throw an error if the GET request fails.
 */
export const getQuotes = async (token, setQuotes, options = {}) => {
  // Extract searchInput and nextPage from options
  const {
    searchInput = '',
    nextPage = null,
    fulfilledFilter = false,
    supplierFilter = '',
    userFilter = '',
    sortBy = '',
    sortDirection = '',
    signal = undefined,
  } = options;

  // If nextPage is provided, use it as the URL; otherwise, start on the first page
  let url = nextPage ? nextPage : `${BACKEND_URL}quotes/?page_num=1`;

  // Apply filter/sort params only on first-page requests.
  if (!nextPage) {
    // If there is a search input, add it to the URL
    if (searchInput) {
      url += `&search=${searchInput}`;
    }

    // If a supplier id value is provided
    if (supplierFilter) {
      url += `&supplier_filter=${supplierFilter}`;
    }
    if (userFilter) {
      url += `&user_id=${userFilter}`;
    }

    if (fulfilledFilter) url += `&fulfilled_filter=True`;
    if (sortBy) {
      url += `&sort_by=${sortBy}`;
    }
    if (sortDirection) {
      url += `&sort_direction=${sortDirection}`;
    }
  }

  // Attempt to send a GET request to the url
  try {
    const response = await axios.get(url, {
      headers: {
        Authorization: `Token ${token}`,
      },
      signal,
    });

    // Save the URL for the next page, if there is one
    const nextCursor = response.data.next;

    // If there is no next page...
    if (!nextCursor) {
      // If it's not the first page, append the new quotes to the existing list
      if (nextPage) {
        setQuotes(prevQuotes => {
          const existingIds = new Set(prevQuotes.map(q => q.id));
          const newResults = response.data.results.filter(q => !existingIds.has(q.id));
          return [...prevQuotes, ...newResults];
        });
      } else {
        // If it is the first page, replace the existing list with the new quotes
        setQuotes(response.data.results);
      }

      // Return an object indicating success and that the end has been reached
      return { success: true, reachedEnd: true };
    }

    // If it's the first page, replace the existing list with the new quotes
    if (!nextPage) {
      setQuotes(response.data.results);
    } else {
      // If it's not the first page, append the new quotes to the existing list
      setQuotes(prevQuotes => {
        const existingIds = new Set(prevQuotes.map(q => q.id));
        const newResults = response.data.results.filter(q => !existingIds.has(q.id));
        return [...prevQuotes, ...newResults];
      });
    }

    // Return an object indicating success and the URL of the next page
    return { success: true, nextPage: response.data.next };

    // If any error occurs during the GET request...
  } catch (error) {
    if (isCanceledRequest(error)) {
      return { canceled: true };
    }
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT_QUOTE, 'getQuotes failed', {
      error: errPayload,
      options,
    });
    return { success: false, error: errPayload };
  }
};

/**
 * Retrieve the details of a specific quote.
 *
 * This function sends a GET request to the backend API to retrieve the details of a specific quote using its quoteId.
 * It accepts a token, a quoteId, and a callback function for setting quote details as arguments.
 * The response from the API is then used to update the quote details. The function returns an object with 'success' (true) key.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {string} quoteId - The ID of the quote.
 * @param {function} setQuoteDetails - The function to set quote details state.
 * @returns {Promise<Object>} - An object with 'success' key.
 * @throws Will throw an error if the get quote details request fails.
 */
export const getQuoteDetails = async (token, quoteId, setQuoteDetails) => {
  // Try to send a GET request to retrieve the details of a specific quote using the provided quoteId
  try {
    const response = await axios.get(`${BACKEND_URL}quotes/${quoteId}/`, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });

    // If the request is successful, use the response to update quote details
    setQuoteDetails(response.data);

    // Return success object and data
    return { success: true, quoteData: response.data };

    // If any error occurs during the retrieval of the quote details
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT_QUOTE, 'getQuoteDetails failed', {
      quoteId,
      error: errPayload,
    });
    return { success: false, error: errPayload };
  }
};

/**
 * Retrieve a list of open quotes for select list.
 *
 * This function sends a GET request to the backend API to retrieve a list of open quotes for a select list.
 * It accepts a token and a callback function for setting the list of open quotes.
 * The response from the API is then used to update the open quotes. The function returns an object with a 'success' key.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {function} setOpenQuotes - The function to set open quotes state.
 * @returns {Promise<Object>} - An object with 'success' key.
 * @throws Will throw an error if the get open quotes request fails.
 */
export const getOpenQuotesSelectList = async (token, setOpenQuotes) => {
  // Try to send a GET request to the 'serve_open_quotes_select_list/' endpoint of the backend API
  try {
    const response = await axios.get(
      `${BACKEND_URL}quotes/serve_open_quotes_select_list/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    // If the request is successful, use the response to update the state of open quotes
    setOpenQuotes(response.data.quotes);
    // Return an object with success set to true
    return { success: true };

    // If any error occurs during the retrieval of the open quotes
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT_QUOTE, 'getOpenQuotesSelectList failed', {
      error: errPayload,
    });
    return { success: false, error: errPayload };
  }
};

/**
 * Get detailed quote information with lazy loading.
 *
 * Response includes `project: { id, name } | null` when the quote is linked to a project.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {number} quoteId - The ID of the quote to fetch details for.
 * @returns {Promise<Object>} - The detailed quote data.
 * @throws Will throw an error if the request fails.
 */
export const getQuoteDetailsLazy = async (token, quoteId) => {
  try {
    // Check cache first
    const cached = QuoteCache.get(quoteId);
    if (cached) {
      return cached;
    }

    // Send a GET request to the get_details endpoint
    const response = await axios.get(
      `${BACKEND_URL}quotes/${quoteId}/get_details/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    // Cache the result
    QuoteCache.set(quoteId, response.data);

    return response.data;
  } catch (error) {
    logError(LOG_CONTEXT_QUOTE, 'getQuoteDetailsLazy failed', {
      quoteId,
      error,
    });
    throw error;
  }
};

/**
 * Get detailed product information for quote items.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {number} quoteId - The ID of the quote to fetch products for.
 * @returns {Promise<Array>} - Array of detailed product information.
 * @throws Will throw an error if the request fails.
 */
export const getQuoteProducts = async (token, quoteId) => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}quotes/${quoteId}/get_products/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    logError(LOG_CONTEXT_QUOTE, 'getQuoteProducts failed', { quoteId, error });
    throw error;
  }
};

/**
 * Get detailed supplier information for a quote.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {number} quoteId - The ID of the quote to fetch supplier for.
 * @returns {Promise<Object>} - Detailed supplier information.
 * @throws Will throw an error if the request fails.
 */
export const getQuoteSupplier = async (token, quoteId) => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}quotes/${quoteId}/get_supplier/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    logError(LOG_CONTEXT_QUOTE, 'getQuoteSupplier failed', { quoteId, error });
    throw error;
  }
};

/**
 * Upload PDF files to AWS S3 for quotes.
 *
 * This function handles the process of uploading supplied PDF files to AWS S3 through predefined, pre-signed URLs.
 * It uses Axios to send POST requests to each pre-signed URL, which are generated by the backend. The details of these URLs are packed in a FormData object along with the corresponding PDF file.
 * It also creates and manages promises for each upload and handles their resolutions. On successful upload, it updates the status of that upload in an 'uploadStatuses' dictionary.
 * After handling all the uploads, it returns the 'uploadStatuses' object.
 *
 * @async
 * @param {Array} presignedUrls - An array of presigned URL objects. Each object contains the S3 presigned URL details and the associated 'frontend_id'.
 * @param {Array} files - An array of file objects. Each file object contains the file data and associated 'id'.
 * @returns {Promise<Object>} - An object containing 'uploadStatuses' corresponding to each PDF upload.
 * @throws Will throw an error if the number of presigned URLs does not match the number of files or if any file upload fails.
 */
export const uploadQuotePdfsToS3 = async (presignedUrls, files) => {
  // Check if the number of pre-signed URLs matches the number of files
  if (presignedUrls.length !== files.length) {
    throw new Error(
      'The number of presigned URLs must match the number of files.'
    );
  }

  // Initialize the dictionary that will hold the statuses of each upload
  const uploadStatuses = {};

  // Prepare a list of upload jobs - one for each pre-signed URL/file
  const uploadPromises = presignedUrls.map(presignedPostData => {
    // Find the file to upload by matching the 'id' of the file with the 'frontend_id' in the pre-signed URL data
    const file = files.find(f => f.id === presignedPostData.frontend_id);

    // Create a new FormData object
    const formData = new FormData();

    // Loop through the pre-signed URL fields and append them to the FormData object
    // Do not append the 'quote_id' field as this is not needed in the S3 request
    Object.keys(presignedPostData.fields).forEach(key => {
      if (key !== 'quote_id') {
        formData.append(key, presignedPostData.fields[key]);
      }
    });

    // Append the file to the FormData object
    formData.append('file', file.file);

    // Make a POST request to the pre-signed URL, uploading the FormData object
    // On response (successful or not), update the upload status
    return axios.post(presignedPostData.url, formData).then(response => {
      // If the response status is successful (2xx), set the status to 'completed', otherwise 'failed'
      uploadStatuses[presignedPostData.quote_id] =
        response.status >= 200 && response.status < 300
          ? 'completed'
          : 'failed';
      return response;
    });
  });

  // Execute all the upload jobs and wait until all of them finish
  try {
    const responses = await Promise.all(uploadPromises);

    // If any upload failed, throw an error
    responses.forEach((response, index) => {
      if (response.status < 200 || response.status >= 300) {
        throw new Error(`File ${index} upload failed: ${response.statusText}`);
      }
    });

    // Return the statuses of each upload
    return { uploadStatuses: uploadStatuses };

    // If any catch any error during upload and return it
  } catch (error) {
    logError(LOG_CONTEXT_QUOTE, 'uploadQuotePdfsToS3 failed', { error });
    return Object.values(error.response?.data || { error: error.message });
  }
};

/**
 * Upload Excel files to AWS S3 for quotes.
 *
 * This function handles the process of uploading supplied Excel files to AWS S3 through predefined, pre-signed URLs.
 * It uses Axios to send POST requests to each pre-signed URL, which are generated by the backend. The details of these URLs are packed in a FormData object along with the corresponding Excel file.
 * It also creates and manages promises for each upload and handles their resolutions. On successful upload, it updates the status of that upload in an 'uploadStatuses' dictionary.
 * After handling all the uploads, it returns the 'uploadStatuses' object.
 *
 * @async
 * @param {Array} presignedUrls - An array of presigned URL objects. Each object contains the S3 presigned URL details and the associated 'frontend_id'.
 * @param {Array} files - An array of file objects. Each file object contains the file data and associated 'id'.
 * @returns {Promise<Object>} - An object containing 'uploadStatuses' corresponding to each Excel upload.
 * @throws Will throw an error if the number of presigned URLs does not match the number of files or if any file upload fails.
 */
export const uploadQuoteExcelsToS3 = async (presignedUrls, files) => {
  // Check if the number of pre-signed URLs matches the number of files
  if (presignedUrls.length !== files.length) {
    throw new Error(
      'The number of presigned URLs must match the number of files.'
    );
  }

  // Initialize the dictionary that will hold the statuses of each upload
  const uploadStatuses = {};

  // Prepare a list of upload jobs - one for each pre-signed URL/file
  const uploadPromises = presignedUrls.map(presignedPostData => {
    // Find the file to upload by matching the 'id' of the file with the 'frontend_id' in the pre-signed URL data
    const file = files.find(f => f.id === presignedPostData.frontend_id);

    // Create a new FormData object
    const formData = new FormData();

    // Loop through the pre-signed URL fields and append them to the FormData object
    // Do not append the 'quote_id' field as this is not needed in the S3 request
    Object.keys(presignedPostData.fields).forEach(key => {
      if (key !== 'quote_id') {
        formData.append(key, presignedPostData.fields[key]);
      }
    });

    // Append the file to the FormData object
    formData.append('file', file.file);

    // Make a POST request to the pre-signed URL, uploading the FormData object
    // On response (successful or not), update the upload status
    return axios.post(presignedPostData.url, formData).then(response => {
      // If the response status is successful (2xx), set the status to 'completed', otherwise 'failed'
      uploadStatuses[presignedPostData.quote_id] =
        response.status >= 200 && response.status < 300
          ? 'completed'
          : 'failed';
      return response;
    });
  });

  // Execute all the upload jobs and wait until all of them finish
  try {
    const responses = await Promise.all(uploadPromises);

    // If any upload failed, throw an error
    responses.forEach((response, index) => {
      if (response.status < 200 || response.status >= 300) {
        throw new Error(`File ${index} upload failed: ${response.statusText}`);
      }
    });

    // Return the statuses of each upload
    return { uploadStatuses: uploadStatuses };

    // If any catch any error during upload and return it
  } catch (error) {
    logError(LOG_CONTEXT_QUOTE, 'uploadQuoteExcelsToS3 failed', { error });
    return Object.values(error.response?.data || { error: error.message });
  }
};

/**
 * Finalize the upload status of multiple quote PDF files.
 *
 * Sends POST requests to the backend API to update the upload status of quote PDF files.
 * For each quote, sends a request with quote_id and status ('completed' or 'failed').
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {Object} uploadStatuses - An object mapping quote IDs to their upload status.
 * @returns {Promise<Object>} An object with a key "success" and a boolean value indicating whether all API calls were successful. If it fails, returns an array containing error messages.
 * @throws {Error} Will throw an error if the request fails.
 */
export const finalizeQuotePdfUploadStatus = async (token, uploadStatuses) => {
  try {
    // Process each quote status update
    const statusPromises = Object.keys(uploadStatuses).map(quoteId => {
      return axios.post(
        `${BACKEND_URL}quotes/update_quote_upload_status/`,
        { quote_id: parseInt(quoteId), status: uploadStatuses[quoteId] },
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      );
    });

    // Wait for all status updates to complete
    await Promise.all(statusPromises);

    // On success, return an object indicating the success of the operation
    return { success: true };
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT_QUOTE, 'finalizeQuotePdfUploadStatus failed', {
      status: error.response?.status,
      quoteCount: Object.keys(uploadStatuses || {}).length,
    });
    return {
      success: false,
      error: errPayload,
      messages: formatUploadFinalizeErrors(errPayload),
    };
  }
};

/**
 * Process PDF files to extract quote data and create quotes.
 *
 * Sends a POST request to the backend API with frontend-parsed quote payloads (one per PDF).
 * Each payload includes requisition_number, supplier_id, supplier_name_text, budget, items, user_id.
 * Handles S3 upload of PDFs and finalizes upload status per quote.
 *
 * **Split-batch (quote PDF upload):** When the browser does not send a `quotes` body (omitted when
 * `quotesWithUsers` is null/length mismatch), the backend is expected to derive quote data from the
 * uploaded PDFs (e.g. server/OCR). Optional `options.quotelessUserIds` sends per-file `user_id`
 * values in display order (same length as `pdfFiles`) via a `user_ids` JSON field — **requires
 * backend support**; unknown fields are ignored by tolerant APIs.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {File[]} pdfFiles - Array of PDF file objects (one per quote).
 * @param {Array|null} [quotesWithUsers] - Optional. One entry per PDF: { requisition_number, supplier_id, supplier_name_text?, budget?, items, user_id? }. Must match pdfFiles length when provided.
 * @param {Object} [options] - Extra multipart fields when quotes are omitted.
 * @param {(number|null)[]} [options.quotelessUserIds] - Same length as pdfFiles; optional user_id per file when no `quotes` JSON is sent.
 * @returns {Promise<Object>} Result shape includes:
 *   - On success: `success`, `createdQuotes`, `totalCreated`, `errors`, `message`, `toast`, and when the backend returns Google Vision OCR handoff data on the create_from_pdf response:
 *   - `ocrfReviewSummary` — array from API `ocrf_review_summary` (sanitized per-file OCR review rows; preferred for UI).
 *   - `ocrfParseSummary` — array from API `ocrf_parse_summary` (detailed per-file rows; optional debug/support).
 *   - On **4xx** (except 413): same keys may be populated from `error.response.data` so the UI can show OCR tables after failed uploads when the backend includes those arrays.
 * @throws Will throw an error if the request fails.
 */
export const createFromPdf = async (
  token,
  pdfFiles,
  quotesWithUsers = null,
  options = {}
) => {
  try {
    // Prepare files with IDs for tracking
    const filesWithIds = pdfFiles.map((file, index) => ({
      id: `pdf_${Date.now()}_${index}`,
      file: file,
    }));

    // Create initial request data with file metadata
    // Supplier ID is automatically extracted from each PDF file
    const requestData = new FormData();
    filesWithIds.forEach(fileObj => {
      requestData.append('files', fileObj.file);
      requestData.append('file_ids', fileObj.id);
    });

    // Send full frontend-parsed payload (one per PDF) so backend can create quotes from it; file still uploaded to S3 for storage.
    // Payload structure (each element of the "quotes" JSON array):
    //   requisition_number: string
    //   supplier_id: number | null (inner supplier id, e.g. 3075179)
    //   supplier_name_text: string | undefined (supplier name only, e.g. "גטר גרופ בע\"מ")
    //   budget: string | undefined (e.g. "בירימברג שוורץ לירון - CFF USD - 6105706")
    //   items: Array<{ supplier_catalog_number, quantity, unit_price, currency?, product_name? }>
    //   user_id: number | null
    const quotelessUserIds = options?.quotelessUserIds;

    if (quotesWithUsers && quotesWithUsers.length === pdfFiles.length) {
      const quotesPayload = quotesWithUsers.map(q => {
        const rawSupplierId = q.supplier_id;
        const supplier_id = rawSupplierId == null ? null : (typeof rawSupplierId === 'number' ? rawSupplierId : (parseInt(rawSupplierId, 10) || null));
        return {
          requisition_number: q.requisition_number,
          supplier_id,
          supplier_name_text: q.supplier_name_text || undefined,
          budget: q.budget || undefined,
          items: q.items || [],
          user_id: q.user_id ?? null,
        };
      });
      logInfo(LOG_CONTEXT_QUOTE, 'createFromPdf: sending quotes to backend', {
        quoteCount: quotesPayload.length,
        withBudget: quotesPayload.filter(q => q.budget).length,
      });
      requestData.append('quotes', JSON.stringify(quotesPayload));
    } else if (
      Array.isArray(quotelessUserIds) &&
      quotelessUserIds.length === pdfFiles.length
    ) {
      const normalized = quotelessUserIds.map(uid =>
        uid == null ? null : typeof uid === 'number' ? uid : parseInt(uid, 10) || null
      );
      requestData.append('user_ids', JSON.stringify(normalized));
      logInfo(LOG_CONTEXT_QUOTE, 'createFromPdf: quoteless batch with user_ids', {
        fileCount: pdfFiles.length,
        assignedUsers: normalized.filter(u => u != null).length,
      });
    }

    // Send POST request to get presigned URLs and process PDFs
    const response = await axios.post(
      `${BACKEND_URL}quotes/create_from_pdf/`,
      requestData,
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'multipart/form-data',
        },
        // Add timeout and max content length handling
        timeout: 300000, // 5 minutes timeout
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      }
    );

    let result = { success: true };

    // Check if there are presigned URLs for S3 upload
    if (
      response.data.presigned_urls &&
      response.data.presigned_urls.length > 0
    ) {
      // Upload PDFs to S3
      const uploadResult = await uploadQuotePdfsToS3(
        response.data.presigned_urls,
        filesWithIds
      );

      if (uploadResult && uploadResult.uploadStatuses) {
        // Finalize PDF upload status for each quote
        const finalizeResult = await finalizeQuotePdfUploadStatus(
          token,
          uploadResult.uploadStatuses
        );
        if (finalizeResult && !finalizeResult.success) {
          result.success = false;
          handleBatchQuoteFinalizeFailure(
            uploadResult.uploadStatuses,
            finalizeResult
          );
        }
      } else {
        result.success = false;
      }
    }

    // Google Vision OCR handoff (optional): per-file summaries from initial POST body
    const reviewSummary = response.data.ocrf_review_summary;
    const parseSummary = response.data.ocrf_parse_summary;
    result.ocrfReviewSummary = Array.isArray(reviewSummary) ? reviewSummary : [];
    result.ocrfParseSummary = Array.isArray(parseSummary) ? parseSummary : [];
    if (result.ocrfReviewSummary.length || result.ocrfParseSummary.length) {
      logInfo(LOG_CONTEXT_QUOTE, 'createFromPdf: OCR summary fields present', {
        ocrfReviewSummaryCount: result.ocrfReviewSummary.length,
        ocrfParseSummaryCount: result.ocrfParseSummary.length,
      });
    }

    // Add created quotes and processing results to the result
    result.createdQuotes = response.data.created_quotes || [];
    result.totalCreated = response.data.total_created || 0;
    result.errors = response.data.errors || [];
    result.message = response.data.message || 'Quote creation completed.';
    result.toast = () => {
      const message = response.data.total_created
        ? `Quote creation completed. ${response.data.total_created} quotes created.`
        : `PDF processing completed. ${
            response.data.total_created || 0
          } quotes created.`;

      showToast(message, 'success', 'top-right', 3000);
    };

    return result;
  } catch (error) {
    // Enhanced error handling for different types of errors
    if (error.response) {
      // Server responded with error status
      if (error.response.status === 413) {
        return {
          success: false,
          error: 'Request Entity Too Large',
          message:
            'The total file size exceeds the server limit. Please try uploading fewer files at once.',
          details: error.response.data,
        };
      } else if (error.response.status >= 500) {
        return {
          success: false,
          error: 'Server Error',
          message: 'Server encountered an error. Please try again later.',
          details: error.response.data,
        };
      } else {
        const data = error.response?.data || {};
        const reviewSummary = data.ocrf_review_summary;
        const parseSummary = data.ocrf_parse_summary;
        const ocrfReviewSummary = Array.isArray(reviewSummary)
          ? reviewSummary
          : [];
        const ocrfParseSummary = Array.isArray(parseSummary)
          ? parseSummary
          : [];
        if (ocrfReviewSummary.length || ocrfParseSummary.length) {
          logInfo(LOG_CONTEXT_QUOTE, 'createFromPdf: OCR fields on client error response', {
            status: error.response.status,
            ocrfReviewSummaryCount: ocrfReviewSummary.length,
            ocrfParseSummaryCount: ocrfParseSummary.length,
          });
        }
        return {
          success: false,
          error: `HTTP ${error.response.status}`,
          message: data.message || 'Upload failed',
          details: data,
          ocrfReviewSummary,
          ocrfParseSummary,
          errors: data.errors || [],
          createdQuotes: data.created_quotes || [],
          totalCreated: data.total_created || 0,
        };
      }
    } else if (error.request) {
      // Request was made but no response received
      return {
        success: false,
        error: 'Network Error',
        message:
          'No response from server. Please check your connection and try again.',
        details: error.message,
      };
    } else {
      // Something else happened
      return {
        success: false,
        error: 'Upload Error',
        message: error.message || 'Failed to upload PDFs',
        details: error,
      };
    }
  }
};

/**
 * Normalize one Excel bulk-create quote row for `create_from_excel_payloads`.
 *
 * @param {Object} q - Parser/review payload
 * @returns {Object}
 */
function normalizeExcelQuotePayloadRow(q) {
  const rawSupplierId = q.supplier_id;
  const supplier_id =
    rawSupplierId == null
      ? null
      : typeof rawSupplierId === 'number'
        ? rawSupplierId
        : parseInt(rawSupplierId, 10) || null;
  const rawProjectId = q.project_id;
  let project_id = null;
  if (rawProjectId != null && String(rawProjectId).trim() !== '') {
    const parsed = Number(rawProjectId);
    project_id = Number.isFinite(parsed) ? parsed : null;
  }
  return {
    ...q,
    requisition_number: q.requisition_number,
    supplier_id,
    supplier_name_text: q.supplier_name_text || undefined,
    budget: q.budget || undefined,
    items: q.items || [],
    user_id: q.user_id ?? null,
    project_id,
  };
}

/**
 * Create quotes from pre-parsed Excel payloads (bulk quotes) and upload files to S3.
 * Frontend parses Excel in the browser and sends quote payloads + files in a single request.
 * Each quote payload may include optional `user_id` and `project_id`.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {Array} excelFiles - Array of Excel File objects (same files that were parsed).
 * @param {Array} quotesWithUsers - Array of quote payloads from parser/review, each with optional
 *   `user_id` and `project_id`: { requisition_number, supplier_id, items, file_index?, user_id?, project_id? }.
 * @returns {Promise<Object>} Same shape as createFromExcel: { success, createdQuotes, totalCreated, errors, message, toast }.
 */
export const createFromExcelWithPayloads = async (
  token,
  excelFiles,
  quotesWithUsers
) => {
  logInfo(LOG_CONTEXT_QUOTE, 'createFromExcelWithPayloads start', {
    fileCount: excelFiles?.length ?? 0,
    quotesCount: quotesWithUsers?.length ?? 0,
    withProject: (quotesWithUsers || []).filter(q => q.project_id != null).length,
  });
  try {
    const filesWithIds = excelFiles.map((file, index) => ({
      id: `excel_${Date.now()}_${index}`,
      file: file,
    }));

    const requestData = new FormData();
    filesWithIds.forEach(fileObj => {
      requestData.append('files', fileObj.file);
      requestData.append('file_ids', fileObj.id);
    });
    const quotesPayload = (quotesWithUsers || []).map(normalizeExcelQuotePayloadRow);
    requestData.append('quotes', JSON.stringify(quotesPayload));

    const response = await axios.post(
      `${BACKEND_URL}quotes/create_from_excel_payloads/`,
      requestData,
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'multipart/form-data',
        },
        timeout: 300000,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      }
    );

    let result = { success: true };

    if (
      response.data.presigned_urls &&
      response.data.presigned_urls.length > 0
    ) {
      const uploadResult = await uploadQuoteExcelsToS3(
        response.data.presigned_urls,
        filesWithIds
      );
      if (uploadResult && uploadResult.uploadStatuses) {
        const finalizeResult = await finalizeQuotePdfUploadStatus(
          token,
          uploadResult.uploadStatuses
        );
        if (finalizeResult && !finalizeResult.success) {
          result.success = false;
          handleBatchQuoteFinalizeFailure(
            uploadResult.uploadStatuses,
            finalizeResult
          );
        }
      } else {
        result.success = false;
      }
    }

    result.createdQuotes = response.data.created_quotes || [];
    result.totalCreated = response.data.total_created || 0;
    result.errors = response.data.errors || [];
    result.message = response.data.message || 'Quote creation completed.';
    result.toast = () => {
      const message =
        result.totalCreated > 0
          ? `Excel processing completed. ${result.totalCreated} quote(s) created.`
          : `Excel processing completed. ${result.totalCreated || 0} quote(s) created.`;
      showToast(message, 'success', 'top-right', 3000);
    };
    logInfo(LOG_CONTEXT_QUOTE, 'createFromExcelWithPayloads success', {
      totalCreated: result.totalCreated,
      errorsCount: result.errors?.length ?? 0,
      hadPresignedUrls: !!(response.data.presigned_urls && response.data.presigned_urls.length > 0),
    });
    return result;
  } catch (error) {
    logError(LOG_CONTEXT_QUOTE, 'createFromExcelWithPayloads failed', {
      status: error.response?.status,
      message: error.message,
      responseData: error.response?.data,
    });
    if (error.response) {
      if (error.response.status === 413) {
        return {
          success: false,
          error: 'Request Entity Too Large',
          message:
            'The total file size exceeds the server limit. Please try uploading fewer files at once.',
          errors: error.response.data?.errors || [
            error.response.data?.message || 'File too large',
          ],
          details: error.response.data,
        };
      }
      if (error.response.status >= 500) {
        return {
          success: false,
          error: 'Server Error',
          message: 'Server encountered an error. Please try again later.',
          errors: error.response.data?.errors || [
            error.response.data?.message || 'Server error',
          ],
          details: error.response.data,
        };
      }
      const errorData = error.response.data || {};
      return {
        success: false,
        error: `HTTP ${error.response.status}`,
        message: errorData.message || 'Upload failed',
        errors: errorData.errors || [errorData.message || 'Upload failed'],
        details: errorData,
      };
    }
    if (error.request) {
      return {
        success: false,
        error: 'Network Error',
        message:
          'No response from server. Please check your connection and try again.',
        errors: ['Network error: No response from server'],
        details: error.message,
      };
    }
    return {
      success: false,
      error: 'Upload Error',
      message: error.message || 'Failed to create quotes from payloads',
      errors: [error.message || 'Failed to create quotes from payloads'],
      details: error,
    };
  }
};

/**
 * Create quotes from Excel requisition report files.
 *
 * This function sends a POST request to the backend API to create quotes from Excel files containing requisition data.
 * The Excel files should contain requisition data with Hebrew column headers for requisition numbers, supplier catalog numbers,
 * quantities, prices, currencies, and supplier information.
 * This function handles S3 uploads for multiple Excel files and then processes them for quote creation.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {Array} excelFiles - An array of Excel file objects (.xlsx or .xls) to be processed.
 * @returns {Promise<Object|Array>} An object with created quotes, processing statistics, and success status if the request is successful. If the request fails, returns an object with error information.
 * @throws Will throw an error if the request fails.
 */
export const createFromExcel = async (token, excelFiles) => {
  try {
    // Prepare files with IDs for tracking
    const filesWithIds = excelFiles.map((file, index) => ({
      id: `excel_${Date.now()}_${index}`,
      file: file,
    }));

    // Create initial request data with file metadata
    const requestData = new FormData();
    filesWithIds.forEach(fileObj => {
      requestData.append('files', fileObj.file);
      requestData.append('file_ids', fileObj.id);
    });

    // Send POST request to get presigned URLs and process Excel files
    const response = await axios.post(
      `${BACKEND_URL}quotes/create_from_excel/`,
      requestData,
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'multipart/form-data',
        },
        // Add timeout and max content length handling
        timeout: 300000, // 5 minutes timeout
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      }
    );

    let result = { success: true };

    // Check if there are presigned URLs for S3 upload
    if (
      response.data.presigned_urls &&
      response.data.presigned_urls.length > 0
    ) {
      // Upload Excel files to S3
      const uploadResult = await uploadQuoteExcelsToS3(
        response.data.presigned_urls,
        filesWithIds
      );

      if (uploadResult && uploadResult.uploadStatuses) {
        // Finalize Excel upload status for each quote
        const finalizeResult = await finalizeQuotePdfUploadStatus(
          token,
          uploadResult.uploadStatuses
        );
        if (finalizeResult && !finalizeResult.success) {
          result.success = false;
          handleBatchQuoteFinalizeFailure(
            uploadResult.uploadStatuses,
            finalizeResult
          );
        }
      } else {
        result.success = false;
      }
    }

    // Add created quotes and processing results to the result
    result.createdQuotes = response.data.created_quotes || [];
    result.totalCreated = response.data.total_created || 0;
    result.errors = response.data.errors || [];
    result.message = response.data.message || 'Quote creation completed.';
    result.toast = () => {
      const message = response.data.total_created
        ? `Excel processing completed. ${response.data.total_created} quote(s) created.`
        : `Excel processing completed. ${
            response.data.total_created || 0
          } quote(s) created.`;
      showToast(message, 'success', 'top-right', 3000);
    };

    return result;
  } catch (error) {
    // Enhanced error handling for different types of errors
    if (error.response) {
      // Server responded with error status
      if (error.response.status === 413) {
        return {
          success: false,
          error: 'Request Entity Too Large',
          message:
            'The total file size exceeds the server limit. Please try uploading fewer files at once.',
          errors: error.response.data?.errors || [
            error.response.data?.message || 'File too large',
          ],
          details: error.response.data,
        };
      } else if (error.response.status >= 500) {
        return {
          success: false,
          error: 'Server Error',
          message: 'Server encountered an error. Please try again later.',
          errors: error.response.data?.errors || [
            error.response.data?.message || 'Server error',
          ],
          details: error.response.data,
        };
      } else {
        // Handle 400 and other client errors
        const errorData = error.response.data || {};
        return {
          success: false,
          error: `HTTP ${error.response.status}`,
          message: errorData.message || 'Upload failed',
          errors: errorData.errors || [errorData.message || 'Upload failed'],
          details: errorData,
        };
      }
    } else if (error.request) {
      // Request was made but no response received
      return {
        success: false,
        error: 'Network Error',
        message:
          'No response from server. Please check your connection and try again.',
        errors: ['Network error: No response from server'],
        details: error.message,
      };
    } else {
      // Something else happened
      return {
        success: false,
        error: 'Upload Error',
        message: error.message || 'Failed to upload Excel files',
        errors: [error.message || 'Failed to upload Excel files'],
        details: error,
      };
    }
  }
};
